

export interface iTrainingData {
    id: string;
    roleInstruction: string;
    instruction: string
    roleQuestion: roles;
    question: string;
    roleAnswer: roles;
    answer: string;
}

type roles = 'system' | 'user' | 'assistant';

export interface iRawTrainingData {
    messages: IMessage[];
    rowId?: number;
}

interface IMessage {
    role?: roles;
    content?: string;
    messageId?: number;
}


export async function getTrainingData(): Promise<iRawTrainingData[]> {
    try {
        const response = await fetch('/src/pages/trainingData/trainingfiles/training.jsonl');
        const text = await response.text();

        const trainingData: iRawTrainingData[] = text
            .trim()
            .split('\n')
            .filter(line => line.trim())
            .map((line, index) => {
                const parsed: iRawTrainingData = JSON.parse(line);
                if (parsed.rowId === undefined) {
                    parsed.rowId = index;
                }

                for (const msg of parsed.messages) {
                    if (msg.role === 'system') {
                        msg.messageId = 0
                    } else if (msg.role === 'user') {
                        msg.messageId = 1
                    } else if (msg.role === 'assistant') {
                        msg.messageId = 2
                    } else {
                        msg.messageId = 3
                    }
                }
                return parsed;

            });

        return trainingData;
    } catch (error) {
        console.error('Error reading training data:', error);
        return [];
    }
}

export async function saveTrainingData(data: iRawTrainingData[]): Promise<void> {
    // Convert to JSONL format (each object on a separate line)
    const jsonlContent = data
        .map(item => JSON.stringify(item))
        .join('\n');

    // Get current date for filename
    const date = new Date();
    const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD format
    const filename = `trained-data-${dateStr}.jsonl`;

    // Create a blob and download the file
    const blob = new Blob([jsonlContent], { type: 'application/jsonl' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export async function generateTrainingJSON(): Promise<void> {
    try {
        // Read the JSONL file
        const response = await fetch('/src/pages/trainingData/trainingfiles/training.jsonl');
        const text = await response.text();

        const rawData: iRawTrainingData[] = text
            .trim()
            .split('\n')
            .filter(line => line.trim())
            .map(line => JSON.parse(line));

        // Convert to structured JSON format
        const trainingData = rawData.map(item => {
            const formattedItem: Record<string, string> = {};
            item.messages.forEach(msg => {
                if (msg.role && msg.content) {
                    if (!formattedItem[msg.role]) {
                        formattedItem[msg.role] = msg.content;
                    } else {
                        // If multiple messages with the same role, concatenate them
                        formattedItem[msg.role] += '\n' + msg.content;
                    }
                }
            });
            return formattedItem;
        });

        // Create JSON string
        const jsonContent = JSON.stringify(trainingData, null, 2);

        // Get current date for filename
        const date = new Date();
        const dateStr = date.toISOString().split('T')[0];
        const filename = `training-structured-${dateStr}.json`;

        // Create a blob and download the file
        const blob = new Blob([jsonContent], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error generating training JSON:', error);
        throw error;
    }
}

export async function createJSONLfromHenrikCV(): Promise<void> {
    try {
        // Read the Henrik CV markdown file
        const response = await fetch('/src/pages/trainingData/trainingfiles/henrikCV.md');
        const mdContent = await response.text();

        // System instruction (extracted from the CV file)
        const systemInstruction = "You are an AI assistant that represents Henrik Ralvert in a professional context. Your primary audience is recruiters, potential clients, and business stakeholders. Your task is to answer questions about Henrik Ralvert's background, experience, competencies, and professional value. Always be factual and precise. Do not invent information. If information is missing, explicitly state that it is not available. Adapt answers to the context (recruitment vs client inquiry). Use professional, clear, and confident language. Avoid exaggeration or marketing clichés. Respond in third person ('Henrik Ralvert') unless explicitly requested otherwise. Respond in the same language as the question.";

        // Parse Q&A pairs from the markdown
        const qaRegex = /Q:\s*(.+?)\nA:\s*\n(.+?)(?=\n\nQ:|$)/gs;
        const matches = [...mdContent.matchAll(qaRegex)];

        const trainingData: iRawTrainingData[] = matches.map((match, index) => ({
            messages: [
                { role: 'system', content: systemInstruction, messageId: 0 },
                { role: 'user', content: match[1].trim(), messageId: 1 },
                { role: 'assistant', content: match[2].trim(), messageId: 2 }
            ],
            rowId: index
        }));

        // Validate the training data
        const validationErrors: string[] = [];
        trainingData.forEach((item, index) => {
            if (!item.messages || !Array.isArray(item.messages)) {
                validationErrors.push(`Row ${index}: Missing or invalid 'messages' array`);
            } else {
                if (item.messages.length !== 3) {
                    validationErrors.push(`Row ${index}: Expected 3 messages, found ${item.messages.length}`);
                }
                item.messages.forEach((msg, msgIndex) => {
                    if (!msg.role) {
                        validationErrors.push(`Row ${index}, Message ${msgIndex}: Missing 'role'`);
                    }
                    if (!msg.content) {
                        validationErrors.push(`Row ${index}, Message ${msgIndex}: Missing 'content'`);
                    }
                    if (msg.role && !['system', 'user', 'assistant'].includes(msg.role)) {
                        validationErrors.push(`Row ${index}, Message ${msgIndex}: Invalid role '${msg.role}'`);
                    }
                });
            }
        });

        if (validationErrors.length > 0) {
            console.error('Validation errors:', validationErrors);
            alert(`Validation failed with ${validationErrors.length} errors. Check console for details.`);
            return;
        }

        // Convert to JSONL format
        const jsonlContent = trainingData
            .map(item => {
                // Remove rowId before saving
                const { rowId, ...itemWithoutRowId } = item;
                return JSON.stringify(itemWithoutRowId);
            })
            .join('\n');

        // Get current date for filename
        const date = new Date();
        const dateStr = date.toISOString().split('T')[0];
        const filename = `henrikCV-training-${dateStr}.jsonl`;

        // Create a blob and download the file
        const blob = new Blob([jsonlContent], { type: 'application/jsonl' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);

        console.log(`✓ Validation passed! Created ${trainingData.length} training examples.`);
        alert(`✓ Success! Created ${trainingData.length} validated training examples.`);
    } catch (error) {
        console.error('Error creating JSONL from Henrik CV:', error);
        alert('Error creating JSONL file. Check console for details.');
        throw error;
    }
}